import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { SqlBuilderButton } from '@twasta/page-runtime/designerKit'
import {
  ArrowLeft, Loader2, Plus, Save, Trash2, Pencil, X, Zap,
} from 'lucide-react'
import {
  getAssistantIntents, saveAssistantIntents, getAssistantConfig,
  type AssistantIntentDef,
} from '@/api/designers'
import ConnectionPicker from '@/components/connections/ConnectionPicker'
import { useFunctionalRoles } from '@/components/common/PolicyPickers'
import { ProfileCheckboxes, useProfileChoices } from './AssistantAccessControls'

// ── Intents tab of the Configure AI page ────────────────────────────
// Designer-defined intents (Assistant_Config/intents.json): each one is a
// pre-configured task — trigger phrases + typed parameters + a fixed
// fulfillment (SQL lookup / fixed text / proposed action / add transaction /
// REST call / local API call / IOFlow call / navigation) — that the runtime Assistant serves as specified,
// giving the designer fine-grained control over specific requests.

type IntentParam = NonNullable<AssistantIntentDef['parameters']>[number]

const inp: React.CSSProperties = {
  padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 6,
  fontSize: 15, background: 'var(--color-bg)', color: 'var(--color-text)', outline: 'none',
}
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 15 }
const mono: React.CSSProperties = { ...inp, fontFamily: 'monospace', fontSize: 14 }

const METHOD_LABEL: Record<string, string> = {
  sql: 'SQL lookup', fixed_text: 'Fixed answer', action: 'Propose action',
  add_transaction: 'Add Transaction',
  rest_api: 'REST API call', call_local_api: 'Call Local API', call_ioflow: 'Call IOFLOW',
  navigate: 'Navigate',
  visual: 'Render a visual', report: 'Render a report',
}

function blankIntent(): AssistantIntentDef {
  return {
    intent_id: '', description: '', detail: '', enabled: true, phrases: [],
    parameters: [],
    fulfillment: { method: 'sql', sql: '' },
    response: { format: 'auto', template: '', no_data_message: '' },
    confirmation: { required: false, prompt: '' },
    access: { allow_roles: [], deny_roles: [] },
  }
}

export default function AssistantIntentsTab({ project, readOnly }: { project: string; readOnly: boolean }) {
  const [intents, setIntents] = useState<AssistantIntentDef[]>([])
  // The profiles the Assistant itself is granted to (Assistant tab). An intent
  // can only ever be served to somebody who can open the Assistant, so the
  // per-intent picker offers exactly those — no rule that can never fire.
  const [grantedProfiles, setGrantedProfiles] = useState<string[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editIndex, setEditIndex] = useState<number | null>(null) // index into intents; -1 = new
  const [draft, setDraft] = useState<AssistantIntentDef | null>(null)

  const refresh = useCallback(() => {
    if (!project) return
    setLoading(true)
    getAssistantIntents(project)
      .then((r) => setIntents(r.intents || []))
      .catch(() => toast.error('Failed to load intents'))
      .finally(() => setLoading(false))
  }, [project])
  useEffect(() => { refresh() }, [refresh])

  useEffect(() => {
    if (!project) return
    getAssistantConfig(project)
      .then((c) => {
        const a = (c.config?.assistant || {}) as { access?: { mode?: string; allow_roles?: string[] } }
        // mode 'all' → null, meaning "every profile the project defines".
        setGrantedProfiles(a.access?.mode === 'roles' ? (a.access.allow_roles || []) : null)
      })
      .catch(() => setGrantedProfiles(null))
  }, [project])

  const persist = async (next: AssistantIntentDef[]) => {
    setSaving(true)
    try {
      await saveAssistantIntents(project, next)
      setIntents(next)
      toast.success('Intents saved')
      return true
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Failed to save intents')
      return false
    } finally { setSaving(false) }
  }

  const startEdit = (i: number) => {
    setEditIndex(i)
    setDraft(JSON.parse(JSON.stringify(i === -1 ? blankIntent() : intents[i])))
  }

  const saveDraft = async () => {
    if (!draft) return
    const id = (draft.intent_id || '').trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '')
    if (!id) { toast.error('Enter an intent ID'); return }
    const dup = intents.some((it, i) => i !== editIndex && it.intent_id.toUpperCase() === id)
    if (dup) { toast.error('An intent with that ID already exists'); return }
    const cleaned: AssistantIntentDef = {
      ...draft,
      intent_id: id,
      phrases: (draft.phrases || []).map((p) => p.trim()).filter(Boolean),
      parameters: (draft.parameters || []).filter((p) => (p.name || '').trim()),
    }
    const next = [...intents]
    if (editIndex === -1) next.push(cleaned)
    else if (editIndex !== null) next[editIndex] = cleaned
    if (await persist(next)) { setEditIndex(null); setDraft(null) }
  }

  const doDelete = async (i: number) => {
    if (!window.confirm(`Delete intent "${intents[i].intent_id}"? This cannot be undone.`)) return
    await persist(intents.filter((_, j) => j !== i))
  }

  const toggleEnabled = async (i: number) => {
    const next = intents.map((it, j) => j === i ? { ...it, enabled: it.enabled === false } : it)
    await persist(next)
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-secondary)' }}><Loader2 className="animate-spin" /></div>

  if (editIndex !== null && draft) {
    return <IntentEditor draft={draft} setDraft={setDraft} saving={saving} readOnly={readOnly}
      project={project} grantedProfiles={grantedProfiles}
      onSave={saveDraft} onBack={() => { setEditIndex(null); setDraft(null) }} />
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-secondary)', flex: 1 }}>
          Intents give you fine-grained control over specific requests: when a user's message matches an intent's
          purpose or trigger phrases, the Assistant serves it <b>exactly</b> as defined here — a parameterized SQL
          lookup, a fixed answer, a proposed action (user reviews &amp; confirms), creating a transaction the user names in their request, a REST call, a local API call, an IOFlow call, navigation guidance,
          or a defined visual / report rendered inline — instead of composing its own behaviour. An intent can also
          be invoked by <b>scanning a QR / barcode</b> (see the intent's Scan section) and from a transaction form's
          <b> action button</b> (handler "AI Assistant" — passing values from the open record). Everything else keeps
          working as before.
        </p>
        {!readOnly && (
          <button onClick={() => startEdit(-1)} style={{ ...inp, cursor: 'pointer', background: 'var(--color-accent)', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
            <Plus size={15} /> New Intent
          </button>
        )}
      </div>

      {intents.length === 0 ? (
        <div style={{ padding: 36, textAlign: 'center', color: 'var(--color-text-secondary)', border: '1px dashed var(--color-border)', borderRadius: 10 }}>
          <Zap size={26} style={{ opacity: 0.5 }} />
          <div style={{ marginTop: 8, fontSize: 15 }}>No intents defined yet.</div>
          <div style={{ fontSize: 13 }}>Define one to control exactly how the Assistant serves a specific request.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {intents.map((it, i) => (
            <div key={it.intent_id + i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, border: '1px solid var(--color-border)', borderRadius: 10, opacity: it.enabled === false ? 0.55 : 1 }}>
              <Zap size={18} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <strong style={{ fontSize: 15 }}>{it.intent_id}</strong>
                  <span style={{ fontSize: 13, padding: '1px 7px', borderRadius: 999, background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}>
                    {METHOD_LABEL[it.fulfillment?.method || ''] || it.fulfillment?.method || '—'}
                  </span>
                  {it.confirmation?.required && (
                    <span style={{ fontSize: 13, padding: '1px 7px', borderRadius: 999, background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}>confirm first</span>
                  )}
                  {it.scan?.enabled && (
                    <span style={{ fontSize: 13, padding: '1px 7px', borderRadius: 999, background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}>scan</span>
                  )}
                  {it.enabled === false && (
                    <span style={{ fontSize: 13, padding: '1px 7px', borderRadius: 999, background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}>disabled</span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {it.description || '—'}
                  {(it.parameters?.length || 0) > 0 && ` · ${it.parameters!.length} parameter${it.parameters!.length === 1 ? '' : 's'}`}
                </div>
              </div>
              {!readOnly && (
                <label style={{ ...row, fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  <input type="checkbox" checked={it.enabled !== false} onChange={() => toggleEnabled(i)} disabled={saving} /> Enabled
                </label>
              )}
              <button onClick={() => startEdit(i)} style={{ ...inp, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Pencil size={14} /> {readOnly ? 'View' : 'Edit'}
              </button>
              {!readOnly && (
                <button onClick={() => doDelete(i)} title="Delete" style={{ ...inp, cursor: 'pointer', color: 'var(--color-error, #dc2626)' }}><Trash2 size={14} /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Single-intent editor ────────────────────────────────────────────
function IntentEditor({ draft, setDraft, saving, readOnly, project, grantedProfiles, onSave, onBack }: {
  draft: AssistantIntentDef
  setDraft: React.Dispatch<React.SetStateAction<AssistantIntentDef | null>>
  saving: boolean
  readOnly: boolean
  project: string
  /** Profiles the Assistant is granted to; null = every profile (mode 'all'). */
  grantedProfiles: string[] | null
  onSave: () => void
  onBack: () => void
}) {
  const set = <K extends keyof AssistantIntentDef>(k: K, v: AssistantIntentDef[K]) =>
    setDraft((d) => (d ? { ...d, [k]: v } : d))
  const ful = draft.fulfillment || { method: 'sql' as const }
  // Functional, so two patches in a row (the query builder applies the SQL and
  // then the database it was built against) both land.
  const setFul = (patch: Partial<NonNullable<AssistantIntentDef['fulfillment']>>) =>
    setDraft((d) => (d ? { ...d, fulfillment: { ...(d.fulfillment || { method: 'sql' as const }), ...patch } } : d))
  const resp = draft.response || {}
  const conf = draft.confirmation || {}
  const access = draft.access || {}
  const scan = draft.scan || {}
  const params = draft.parameters || []
  const phrases = draft.phrases || []

  const setParam = (i: number, patch: Partial<IntentParam>) =>
    set('parameters', params.map((p, j) => (j === i ? { ...p, ...patch } : p)))

  // Profiles offered for this intent: the Assistant's grant when it is
  // restricted, otherwise the project's whole role catalogue. Roles already on
  // the intent are always included so an obsolete one stays removable. Both
  // lists are computed unconditionally — hooks can't live behind a branch.
  const funcRoles = useFunctionalRoles(project)
  const referencedRoles = [...(access.allow_roles || []), ...(access.deny_roles || [])]
  const allChoices = useProfileChoices(funcRoles, referencedRoles)
  const grantedChoices = useProfileChoices(grantedProfiles || [], referencedRoles, false)
  const intentProfileChoices = grantedProfiles ? grantedChoices : allChoices

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <button onClick={onBack} style={{ ...inp, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}><ArrowLeft size={15} /> Intents</button>
        <strong style={{ fontSize: 16 }}>{draft.intent_id || 'New intent'}</strong>
        {!readOnly && (
          <button onClick={onSave} disabled={saving} style={{ ...inp, marginLeft: 'auto', cursor: 'pointer', background: 'var(--color-accent)', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
          <Field label="Intent ID" hint="Short unique identifier (A-Z, 0-9, _).">
            <input style={inp} value={draft.intent_id} disabled={readOnly} placeholder="e.g. ORDER_STATUS"
              onChange={(e) => set('intent_id', e.target.value.toUpperCase())} />
          </Field>
          <Field label="Description" hint="What this intent does — the Assistant uses it to match requests.">
            <input style={inp} value={draft.description || ''} disabled={readOnly}
              placeholder="e.g. Look up the delivery status of a sales order"
              onChange={(e) => set('description', e.target.value)} />
          </Field>
        </div>

        <Field label="When to use (optional)" hint="Extra guidance for the Assistant on when this intent applies (and when it doesn't).">
          <input style={inp} value={draft.detail || ''} disabled={readOnly}
            onChange={(e) => set('detail', e.target.value)} />
        </Field>

        <Field label="Trigger phrases" hint="Example user requests this intent should match (matched on meaning, not exact words). Use {parameter} placeholders.">
          {phrases.map((p, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <input style={{ ...inp, flex: 1 }} value={p} disabled={readOnly}
                placeholder='e.g. what is the status of order {order_no}'
                onChange={(e) => set('phrases', phrases.map((x, j) => (j === i ? e.target.value : x)))} />
              {!readOnly && <button onClick={() => set('phrases', phrases.filter((_, j) => j !== i))} style={{ ...inp, cursor: 'pointer' }}><X size={13} /></button>}
            </div>
          ))}
          {!readOnly && <button onClick={() => set('phrases', [...phrases, ''])} style={{ ...inp, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, width: 'fit-content' }}><Plus size={13} /> Add phrase</button>}
        </Field>

        <Field label="Parameters" hint="Values the intent needs. Source 'From the user' is filled from the user's words (the Assistant asks if a required one is missing); 'From the user's profile' from their identity; 'Fixed value' always uses the default.">
          {params.map((p, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.6fr 0.8fr 0.9fr 1fr auto', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <input style={inp} value={p.name} disabled={readOnly} placeholder="name"
                onChange={(e) => setParam(i, { name: e.target.value.toLowerCase().replace(/[^a-z0-9_]+/g, '_') })} />
              <input style={inp} value={p.description || ''} disabled={readOnly} placeholder="description"
                onChange={(e) => setParam(i, { description: e.target.value })} />
              <select style={inp} value={p.type || 'text'} disabled={readOnly}
                onChange={(e) => setParam(i, { type: e.target.value as IntentParam['type'] })}>
                <option value="text">text</option><option value="number">number</option><option value="date">date</option>
              </select>
              <select style={inp} value={p.source || 'user'} disabled={readOnly}
                onChange={(e) => setParam(i, { source: e.target.value as IntentParam['source'] })}>
                <option value="user">From the user</option>
                <option value="profile">User's profile</option>
                <option value="fixed">Fixed value</option>
              </select>
              {p.source === 'profile' ? (
                <select style={inp} value={p.profile_field || 'email'} disabled={readOnly}
                  onChange={(e) => setParam(i, { profile_field: e.target.value as IntentParam['profile_field'] })}>
                  <option value="email">email</option><option value="username">username</option>
                  <option value="user_id">user_id</option><option value="roles">roles</option>
                </select>
              ) : (
                <input style={inp} value={p.default || ''} disabled={readOnly} placeholder="default"
                  onChange={(e) => setParam(i, { default: e.target.value })} />
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ ...row, fontSize: 13 }} title="Required">
                  <input type="checkbox" checked={!!p.required} disabled={readOnly}
                    onChange={(e) => setParam(i, { required: e.target.checked })} /> req
                </label>
                {!readOnly && <button onClick={() => set('parameters', params.filter((_, j) => j !== i))} style={{ ...inp, cursor: 'pointer', padding: 6 }}><X size={13} /></button>}
              </div>
            </div>
          ))}
          {!readOnly && (
            <button onClick={() => set('parameters', [...params, { name: '', type: 'text', source: 'user', required: false }])}
              style={{ ...inp, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, width: 'fit-content' }}>
              <Plus size={13} /> Add parameter
            </button>
          )}
        </Field>

        <Field label="Fulfillment" hint="What the Assistant does when this intent runs.">
          <select style={{ ...inp, width: 260 }} value={ful.method} disabled={readOnly}
            onChange={(e) => setFul({ method: e.target.value as NonNullable<AssistantIntentDef['fulfillment']>['method'] })}>
            <option value="sql">SQL lookup (read-only SELECT)</option>
            <option value="fixed_text">Fixed answer text</option>
            <option value="action">Propose an object action</option>
            <option value="add_transaction">Add Transaction</option>
            <option value="rest_api">Call a REST API</option>
            <option value="call_local_api">Call Local API</option>
            <option value="call_ioflow">Call IOFLOW</option>
            <option value="navigate">Navigate the user to a screen</option>
            <option value="visual">Render a defined Visual inline</option>
            <option value="report">Render a Jasper Report inline</option>
          </select>
        </Field>

        {ful.method === 'sql' && (
          <>
            <Field
              label="Runs on"
              hint="Which database answers this lookup. Leave it on the project database (the default) to query the app's own data; pick a registered connection — an in-memory replica, say — to run the same SELECT there instead.">
              <ConnectionPicker
                project={project}
                value={ful.connection_id || ''}
                disabled={readOnly}
                onChange={(cid) => setFul({ connection_id: cid })} />
            </Field>
            <Field label="SQL" hint={ful.connection_id
              ? 'A single read-only SELECT against the connection above. Bind parameters as :name (e.g. WHERE ord_no = :order_no). Rows are capped at runtime.'
              : "A single read-only SELECT against the app's data. Bind parameters as :name (e.g. WHERE ord_no = :order_no). Rows are capped at runtime."}>
              {!readOnly && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                  <SqlBuilderButton
                    value={ful.sql || ''}
                    onChange={(sql) => setFul({ sql })}
                    project={project}
                    target={ful.connection_id ? `conn:${ful.connection_id}` : 'project'}
                    onTargetChange={(t) => setFul({ connection_id: t.startsWith('conn:') ? t.slice(5) : '' })}
                    title="Assistant intent lookup"
                  />
                </div>
              )}
              <textarea style={{ ...mono, minHeight: 110, resize: 'vertical' }} value={ful.sql || ''} disabled={readOnly}
                placeholder="SELECT ord_no, status, delivery_date FROM sales_orders WHERE ord_no = :order_no"
                onChange={(e) => setFul({ sql: e.target.value })} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
              <Field label="Answer format">
                <select style={inp} value={resp.format || 'auto'} disabled={readOnly}
                  onChange={(e) => set('response', { ...resp, format: e.target.value as NonNullable<AssistantIntentDef['response']>['format'] })}>
                  <option value="auto">Auto</option>
                  <option value="sentence">Sentence template</option>
                  <option value="table">Table</option>
                </select>
              </Field>
              <Field label="Sentence template (optional)" hint="Rendered once per row; use {column} and {parameter} placeholders. Leave blank for a table.">
                <input style={mono} value={resp.template || ''} disabled={readOnly}
                  placeholder="Order {order_no} is {status}, expected {delivery_date}."
                  onChange={(e) => set('response', { ...resp, template: e.target.value })} />
              </Field>
            </div>
            <Field label="No-data message (optional)">
              <input style={inp} value={resp.no_data_message || ''} disabled={readOnly}
                placeholder="No order found for {order_no}."
                onChange={(e) => set('response', { ...resp, no_data_message: e.target.value })} />
            </Field>
          </>
        )}

        {ful.method === 'fixed_text' && (
          <Field label="Answer text" hint="Returned verbatim (with {parameter} placeholders filled in).">
            <textarea style={{ ...inp, minHeight: 90, resize: 'vertical' }} value={ful.text || ''} disabled={readOnly}
              onChange={(e) => setFul({ text: e.target.value })} />
          </Field>
        )}

        {ful.method === 'action' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Object" hint="A data-entry object of this app (as in WHAT YOU CAN CHANGE).">
                <input style={inp} value={ful.obj_name || ''} disabled={readOnly} placeholder="e.g. leave_request"
                  onChange={(e) => setFul({ obj_name: e.target.value })} />
              </Field>
              <Field label="Action">
                <input style={inp} value={ful.action || 'add'} disabled={readOnly} placeholder="add / edit / delete / custom action"
                  onChange={(e) => setFul({ action: e.target.value })} />
              </Field>
            </div>
            <Field label="Field values" hint="Object field → value template ({parameter} placeholders). Leave empty to pass the parameters through by name. The user always reviews and confirms before it runs — and the 'Change data' capability must be enabled.">
              <KeyValueRows value={ful.value_map || {}} readOnly={readOnly} keyPlaceholder="field" valPlaceholder="{parameter}"
                onChange={(m) => setFul({ value_map: m })} />
            </Field>
          </>
        )}

        {ful.method === 'add_transaction' && (
          <>
            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
              The user names the transaction to create — by its <b>menu name</b> or its
              <b> object name</b> — and the field values, right in their request
              (e.g. “create a Customers transaction with cust_name: ABC and cust_descrp: ABC”).
              The Assistant resolves the target, extracts the fields, and proposes the add;
              the user reviews &amp; confirms before it runs, and the “Change data” capability
              must be enabled.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
              <Field label="Default object (optional)" hint="Pin a transaction to create when the user doesn't name one. Leave blank to always take the target from the request.">
                <input style={inp} value={ful.obj_name || ''} disabled={readOnly} placeholder="e.g. customers"
                  onChange={(e) => setFul({ obj_name: e.target.value })} />
              </Field>
            </div>
            <Field label="Pinned field values (optional)" hint="Field → value template ({parameter} placeholders) always set on the new record (e.g. a status or a branch constant). The values the user gives in the prompt take priority.">
              <KeyValueRows value={ful.value_map || {}} readOnly={readOnly} keyPlaceholder="field" valPlaceholder="{parameter} or constant"
                onChange={(m) => setFul({ value_map: m })} />
            </Field>
          </>
        )}

        {ful.method === 'rest_api' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12 }}>
              <Field label="Method">
                <select style={inp} value={ful.http_method || 'GET'} disabled={readOnly}
                  onChange={(e) => setFul({ http_method: e.target.value })}>
                  {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="URL" hint="{parameter} placeholders are URL-encoded and substituted.">
                <input style={mono} value={ful.url || ''} disabled={readOnly}
                  placeholder="https://api.example.com/track?awb={awb_no}"
                  onChange={(e) => setFul({ url: e.target.value })} />
              </Field>
            </div>
            <Field label="Headers (optional)">
              <KeyValueRows value={ful.headers || {}} readOnly={readOnly} keyPlaceholder="Header" valPlaceholder="value"
                onChange={(m) => setFul({ headers: m })} />
            </Field>
            <Field label="Request body (optional)" hint="Sent for non-GET requests (JSON, with {parameter} placeholders).">
              <textarea style={{ ...mono, minHeight: 70, resize: 'vertical' }} value={ful.body || ''} disabled={readOnly}
                onChange={(e) => setFul({ body: e.target.value })} />
            </Field>
          </>
        )}

        {ful.method === 'call_local_api' && (
          <>
            <Field label="Local API URL" hint="POSTed to on every run. {parameter} placeholders are URL-encoded and substituted.">
              <input style={mono} value={ful.url || ''} disabled={readOnly}
                placeholder="http://localhost:8080/api/process"
                onChange={(e) => setFul({ url: e.target.value })} />
            </Field>
            <Field label="PROCESS_RESP" hint="Sent to the local API beside the form values, and returned with its response (and with the error message if the call fails). Leave blank to omit it from the request.">
              <input style={mono} value={ful.process_resp || ''} disabled={readOnly}
                placeholder="e.g. Y"
                onChange={(e) => setFul({ process_resp: e.target.value })} />
            </Field>
            <Field label="Headers (optional)">
              <KeyValueRows value={ful.headers || {}} readOnly={readOnly} keyPlaceholder="Header" valPlaceholder="value"
                onChange={(m) => setFul({ headers: m })} />
            </Field>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              The request body is fixed: the collected form values go over as a JSON string in
              <code style={{ margin: '0 3px' }}>text</code>, with
              <code style={{ margin: '0 3px' }}>PROCESS_RESP</code> beside it — e.g.
              <code style={{ marginLeft: 3 }}>{'{"text": "{\\"order_no\\":\\"SO-1\\"}", "PROCESS_RESP": "Y"}'}</code>.
              The local API's response is returned to the Assistant as-is.
            </div>
          </>
        )}

        {ful.method === 'call_ioflow' && (
          <>
            <Field label="IOFlow configuration URL" hint="Read (GET) on every run. It returns the API configuration — {&quot;url&quot;: …, &quot;headers&quot;: {…}} — and the request is POSTed to that URL with those headers. You can also paste that JSON configuration here directly instead of a URL.">
              <input style={mono} value={ful.config_url || ''} disabled={readOnly}
                placeholder="https://ioflow.example.com/api/config/my-flow"
                onChange={(e) => setFul({ config_url: e.target.value })} />
            </Field>
            <Field label="PROCESS_RESP" hint="Returned with the IOFlow response (and with the error message if the call fails).">
              <input style={mono} value={ful.process_resp || ''} disabled={readOnly}
                placeholder="e.g. Y"
                onChange={(e) => setFul({ process_resp: e.target.value })} />
            </Field>
            <Field label="Result template (optional)" hint="Applied when the response has a results object: rendered once per item (or once for an object) with {field} and {parameter} placeholders.">
              <input style={mono} value={resp.template || ''} disabled={readOnly}
                placeholder="Order {order_no} is {status}"
                onChange={(e) => set('response', { ...resp, template: e.target.value })} />
            </Field>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              The request body is fixed — the parameters above are not sent:
              <code style={{ marginLeft: 3 }}>BIS_INTENT_ID, identity, USER_MSG, IS_BROWSER, INPUT_TEXT, USER_ID, UUID</code>
              (the intent, the caller, their latest message, and the conversation). Fields starting with
              <code style={{ margin: '0 3px' }}>INVOKE</code> are removed before sending. If the response has a
              <code style={{ margin: '0 3px' }}>results</code> object and a Result template is set, the
              template is rendered over it; otherwise the response is returned as-is.
            </div>
          </>
        )}

        {ful.method === 'navigate' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Target" hint="The object / screen name the user should open.">
              <input style={inp} value={ful.target || ''} disabled={readOnly}
                onChange={(e) => setFul({ target: e.target.value })} />
            </Field>
            <Field label="Shown as (optional)">
              <input style={inp} value={ful.target_title || ''} disabled={readOnly}
                onChange={(e) => setFul({ target_title: e.target.value })} />
            </Field>
          </div>
        )}

        {(ful.method === 'visual' || ful.method === 'report') && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field
                label={ful.method === 'visual' ? 'Visual object (obj_type V)' : 'Report object (obj_type R)'}
                hint={ful.method === 'visual'
                  ? 'The defined Visual rendered inline in the chat with the parameters below.'
                  : 'The Jasper Report rendered inline in the chat with the parameters below.'}
              >
                <input style={inp} value={ful.obj_name || ''} disabled={readOnly}
                  placeholder={ful.method === 'visual' ? 'e.g. customer_credit_position' : 'e.g. item_stock_card'}
                  onChange={(e) => setFul({ obj_name: e.target.value })} />
              </Field>
              <Field label="Card title (optional)" hint="Shown above the result; {parameter} placeholders fill in.">
                <input style={inp} value={ful.title || ''} disabled={readOnly}
                  placeholder="e.g. Stock card for {item_code}"
                  onChange={(e) => setFul({ title: e.target.value })} />
              </Field>
            </div>
            <Field
              label={ful.method === 'visual' ? 'Prompt values (visual prompt ← value)' : 'Report parameters (prompt ← value)'}
              hint="Object parameter → value template ({parameter} placeholders). Leave empty to pass the intent's parameters through by name."
            >
              <KeyValueRows value={ful.param_map || {}} readOnly={readOnly}
                keyPlaceholder={ful.method === 'visual' ? 'prompt variable' : 'report prompt'}
                valPlaceholder="{parameter}"
                onChange={(m) => setFul({ param_map: m })} />
            </Field>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              The result renders through the app's secured {ful.method === 'visual' ? 'visual' : 'report'}-data
              route for the calling user, so their read rights apply. The Assistant cannot see into the
              rendered {ful.method} — use an SQL lookup instead when it should reason about the numbers.
            </div>
          </>
        )}

        <Field label="Confirmation">
          <label style={row}>
            <input type="checkbox" checked={!!conf.required} disabled={readOnly}
              onChange={(e) => set('confirmation', { ...conf, required: e.target.checked })} />
            Ask the user to confirm before this intent runs
          </label>
          {conf.required && (
            <input style={{ ...inp, marginTop: 6 }} value={conf.prompt || ''} disabled={readOnly}
              placeholder='Confirmation question, e.g. "Submit the leave request for {from_date}?"'
              onChange={(e) => set('confirmation', { ...conf, prompt: e.target.value })} />
          )}
        </Field>

        <Field
          label="Scan invocation (QR / barcode)"
          hint="Let this intent be invoked by SCANNING a code — from the chat's Scan button (camera or a connected scanner). When exactly one intent matches a scanned code and its required parameters parse out of it, it runs instantly with no AI call; otherwise the Assistant asks."
        >
          <label style={row}>
            <input type="checkbox" checked={!!scan.enabled} disabled={readOnly}
              onChange={(e) => set('scan', { ...scan, enabled: e.target.checked })} />
            This intent can be invoked by scanning a code
          </label>
          {scan.enabled && (
            <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
                <Field label="Code prefix (optional)" hint='Codes starting with this match, e.g. "ITEM:". Stripped before parsing.'>
                  <input style={mono} value={scan.prefix || ''} disabled={readOnly}
                    placeholder="ITEM:"
                    onChange={(e) => set('scan', { ...scan, prefix: e.target.value })} />
                </Field>
                <Field label="Match pattern (optional)" hint={'Regular expression; named groups fill parameters, e.g. ^EMP-(?P<emp_id>\\d+)$. A pattern match beats a prefix match.'}>
                  <input style={mono} value={scan.pattern || ''} disabled={readOnly}
                    placeholder="^EMP-(?P<emp_id>\d+)$"
                    onChange={(e) => set('scan', { ...scan, pattern: e.target.value })} />
                </Field>
              </div>
              <Field label="Whole code goes to (optional)" hint="When the code isn't structured (no pattern groups, JSON or key=value pairs), its whole text fills this parameter. With a single parameter this is automatic.">
                <select style={{ ...inp, width: 280 }} value={scan.target_parameter || ''} disabled={readOnly}
                  onChange={(e) => set('scan', { ...scan, target_parameter: e.target.value })}>
                  <option value="">(auto)</option>
                  {params.filter((p) => (p.name || '').trim() && (p.source || 'user') === 'user').map((p) => (
                    <option key={p.name} value={p.name}>{p.name}</option>
                  ))}
                </select>
              </Field>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                Structured codes also parse automatically: a JSON object ({'{"item_code": "X9"}'}) or
                key=value pairs (item_code=X9;warehouse=WH1) fill matching parameters. With neither a
                prefix nor a pattern this intent is a catch-all for any scanned code.
              </div>
            </div>
          )}
        </Field>

        <Field
          label="Which profiles can use this intent"
          hint="Of the profiles granted the Assistant, who may invoke this intent. Leave the allow list empty for everyone granted; a denied profile is refused even if allowed elsewhere."
        >
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'grid', gap: 5 }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Allowed profiles (empty = everyone granted the Assistant)</span>
              <ProfileCheckboxes choices={intentProfileChoices} value={access.allow_roles || []} disabled={readOnly}
                onChange={(next) => set('access', { ...access, allow_roles: next })} />
            </div>
            <div style={{ display: 'grid', gap: 5 }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Denied profiles</span>
              <ProfileCheckboxes choices={intentProfileChoices} value={access.deny_roles || []} disabled={readOnly}
                onChange={(next) => set('access', { ...access, deny_roles: next })} />
            </div>
          </div>
        </Field>
      </div>
    </div>
  )
}

// key/value editor used for action value_map + REST headers
function KeyValueRows({ value, onChange, readOnly, keyPlaceholder, valPlaceholder }: {
  value: Record<string, string>
  onChange: (m: Record<string, string>) => void
  readOnly: boolean
  keyPlaceholder: string
  valPlaceholder: string
}) {
  const entries = Object.entries(value)
  const setEntry = (i: number, k: string, v: string) => {
    const next = entries.map(([ek, ev], j) => (j === i ? [k, v] : [ek, ev])) as [string, string][]
    onChange(Object.fromEntries(next.filter(([ek]) => ek !== undefined)))
  }
  return (
    <div>
      {entries.map(([k, v], i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <input style={{ ...inp, flex: 1 }} value={k} disabled={readOnly} placeholder={keyPlaceholder}
            onChange={(e) => setEntry(i, e.target.value, v)} />
          <input style={{ ...inp, flex: 2, fontFamily: 'monospace', fontSize: 14 }} value={v} disabled={readOnly} placeholder={valPlaceholder}
            onChange={(e) => setEntry(i, k, e.target.value)} />
          {!readOnly && (
            <button onClick={() => onChange(Object.fromEntries(entries.filter((_, j) => j !== i)))} style={{ ...inp, cursor: 'pointer' }}><X size={13} /></button>
          )}
        </div>
      ))}
      {!readOnly && (
        <button onClick={() => onChange({ ...value, '': '' })} style={{ ...inp, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Plus size={13} /> Add
        </button>
      )}
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 5 }}>
      <div style={{ fontSize: 14, fontWeight: 600 }}>{label}</div>
      {hint && <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{hint}</div>}
      {children}
    </div>
  )
}
